import { Database, FlaskConical, Table2, Telescope } from "lucide-react";
import { formatNumber, formatRelative, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, EmptyState, Skeleton } from "@ncp/ui";
import { usePageData, usePageFile, type PageDetail } from "../api";

const json = (v: unknown) => JSON.stringify(v, null, 2);

/** What the page holds (its collections and datasets), what platform data it reads, and the mock data page:dev serves in their place. */
export function DataTab({ detail }: { detail: PageDetail }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const data = usePageData(detail.page.key);
  const hasMocks = detail.files.some((f) => f.path === "mocks/datasets.json");
  const mocks = usePageFile(detail.page.id, hasMocks ? "mocks/datasets.json" : null);
  const declared = detail.collections ?? [];
  const d = data.data;

  if (data.isLoading) return <div className="grid gap-3 lg:grid-cols-2"><Skeleton className="h-48" /><Skeleton className="h-48" /></div>;

  const mockSets = (() => {
    try { return mocks.data ? Object.entries(JSON.parse(mocks.data.content) as Record<string, unknown[]>) : []; } catch { return []; }
  })();

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Database className="size-4 text-aegold-700" /> {t("admin:pageStudio.data.collections")}</CardTitle><CardDescription>{t("admin:pageStudio.data.collectionsHint")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          {declared.length === 0 && !d?.collections.length ? <p className="text-sm text-muted-foreground">{t("admin:pageStudio.data.noCollections")}</p> : null}
          {declared.map((c) => {
            const live = d?.collections.find((x) => x.key === c.key);
            return (
              <details key={c.key} className="rounded-lg border border-sand-200 bg-white p-3">
                <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                  <span className="font-medium">{c.name ?? c.key}</span>
                  <code className="font-mono text-xs text-muted-foreground" dir="ltr">{c.key}</code>
                  <Badge variant="outline" size="sm">{t(`admin:pageStudio.data.scope.${c.scope ?? "user"}`, { defaultValue: c.scope ?? "user" })}</Badge>
                  {c.classification ? <Badge variant={c.classification === "Confidential" ? "warning" : "outline"} size="sm">{c.classification}</Badge> : null}
                  <span className="ms-auto text-xs text-muted-foreground tabular">{t("admin:pageStudio.data.records", { count: live?.records ?? 0 })}{live?.lastAt ? ` · ${formatRelative(live.lastAt, locale)}` : ""}</span>
                </summary>
                {live?.latest.length ? (
                  <ul className="mt-2 space-y-1.5">
                    {live.latest.map((r) => (
                      <li key={r.id} className="rounded-md bg-sand-50 p-2">
                        <div className="mb-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">{r.key ? <code dir="ltr">{r.key}</code> : null}{r.owner ? <span>{r.owner}</span> : null}{r.space ? <span>{r.space}</span> : null}<span>r{r.revision}</span><span>{formatRelative(r.at, locale)}</span></div>
                        <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px]" dir="ltr">{json(r.data)}</pre>
                      </li>
                    ))}
                  </ul>
                ) : <p className="mt-2 text-xs text-muted-foreground">{t("admin:pageStudio.data.noRecords")}</p>}
                {c.retentionDays ? <p className="mt-2 text-[11px] text-muted-foreground">{t("admin:pageStudio.data.retention", { days: c.retentionDays })}</p> : null}
              </details>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Table2 className="size-4 text-aegold-700" /> {t("admin:pageStudio.data.datasets")}</CardTitle><CardDescription>{t("admin:pageStudio.data.datasetsHint")}</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          {!d?.datasets.length ? <p className="text-sm text-muted-foreground">{t("admin:pageStudio.data.noDatasets")}</p> : d.datasets.map((ds) => (
            <details key={`${ds.key}:${ds.space ?? ""}`} className="rounded-lg border border-sand-200 bg-white p-3">
              <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                <span className="font-medium">{(locale === "ar" && ds.nameAr) || ds.name}</span>
                <code className="font-mono text-xs text-muted-foreground" dir="ltr">{detail.page.key}/{ds.key}</code>
                <Badge variant="outline" size="sm">{t(`admin:pageStudio.data.origin.${ds.origin}`, { defaultValue: ds.origin })}</Badge>
                <span className="ms-auto text-xs text-muted-foreground tabular">{t("admin:pageStudio.data.rows", { count: ds.rows })} · {formatRelative(ds.refreshedAt ?? ds.updatedAt, locale)}</span>
              </summary>
              {ds.columns?.length ? (
                <div className="mt-2 overflow-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="border-b border-sand-200 text-start">{ds.columns.map((c) => <th key={c.key} className="px-2 py-1 text-start font-medium">{(locale === "ar" && c.labelAr) || c.label || c.key}{c.unit ? <span className="text-muted-foreground"> ({c.unit})</span> : null}</th>)}</tr></thead>
                    <tbody>{(ds.sample ?? []).map((row, i) => <tr key={i} className="border-b border-sand-100">{ds.columns!.map((c) => <td key={c.key} className="px-2 py-1 tabular">{String(row[c.key] ?? "")}</td>)}</tr>)}</tbody>
                  </table>
                </div>
              ) : null}
              {ds.lineage?.length ? (
                <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                  {ds.lineage.map((l, i) => <li key={i}>{t("admin:pageStudio.data.source")}: {l.title ?? l.ref ?? l.kind}{l.retrievedAt ? ` · ${formatRelative(l.retrievedAt, locale)}` : ""}</li>)}
                </ul>
              ) : null}
            </details>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Telescope className="size-4 text-aegold-700" /> {t("admin:pageStudio.data.reads")}</CardTitle><CardDescription>{t("admin:pageStudio.data.readsHint")}</CardDescription></CardHeader>
        <CardContent>
          {!d?.reads.byCapability.length ? <EmptyState title={t("admin:pageStudio.data.noReads")} className="py-6" /> : (
            <>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {d.reads.byCapability.map((c) => <Badge key={`${c.capability}:${c.outcome}`} variant={c.outcome === "ok" ? "outline" : "warning"} size="sm"><code dir="ltr">{c.capability}</code> · {formatNumber(c.count, locale)}{c.outcome !== "ok" ? ` · ${c.outcome}` : ""}</Badge>)}
              </div>
              <ul className="divide-y divide-sand-100 text-xs">
                {d.reads.recent.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 py-1.5">
                    <code className="shrink-0 font-mono" dir="ltr">{r.capability}</code>
                    <span className="min-w-0 flex-1 truncate text-muted-foreground" dir="ltr">{r.summary}</span>
                    <span className="shrink-0 text-muted-foreground">{r.agentKey ?? r.caller} · {formatRelative(r.at, locale)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><FlaskConical className="size-4 text-aegold-700" /> {t("admin:pageStudio.data.mocks")}</CardTitle><CardDescription>{t("admin:pageStudio.data.mocksHint")}</CardDescription></CardHeader>
        <CardContent>
          {!hasMocks ? <p className="text-sm text-muted-foreground">{t("admin:pageStudio.data.noMocks")}</p> : mocks.isLoading ? <Skeleton className="h-24" /> : (
            <ul className="space-y-1 text-sm">
              {mockSets.map(([key, rows]) => <li key={key} className="flex items-center gap-2"><code className="font-mono text-xs" dir="ltr">{key}</code><span className="text-xs text-muted-foreground">{t("admin:pageStudio.data.rows", { count: Array.isArray(rows) ? rows.length : 0 })}</span></li>)}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
